import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useAdmin } from '../../context/AdminContext';

import styles from './AdminLayout.module.css';

/**
 * AdminLayout Component
 * Reusable layout wrapper for all admin pages with left sidebar navigation
 */

/**
 *
 */
type AdminLayoutProps = {
  readonly children: ReactNode;
};

const AdminLayout = ({ children }: AdminLayoutProps): JSX.Element => {
  const location = useLocation();
  const { logout } = useAdmin();

  const handleLogout = (): void => {
    logout();
  };

  // Check if current path is active
  const isActive = (path: string): boolean => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  return (
    <div className={styles.adminLayout}>
      {/* Left Sidebar */}
      <aside className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <h2 className={styles.sidebarTitle}>Admin Panel</h2>
        </div>

        <nav className={styles.nav}>
          <Link
            to="/admin/dashboard"
            className={`${styles.navItem} ${isActive('/admin/dashboard') ? styles.active : ''}`}
          >
            <span className={styles.navIcon}>📊</span>
            <span className={styles.navLabel}>Dashboard</span>
          </Link>

          <Link
            to="/admin/menu"
            className={`${styles.navItem} ${isActive('/admin/menu') ? styles.active : ''}`}
          >
            <span className={styles.navIcon}>📋</span>
            <span className={styles.navLabel}>Menu Editor</span>
          </Link>

          <Link
            to="/admin/content"
            className={`${styles.navItem} ${isActive('/admin/content') ? styles.active : ''}`}
          >
            <span className={styles.navIcon}>✏️</span>
            <span className={styles.navLabel}>Content Editor</span>
          </Link>

          <Link
            to="/admin/images"
            className={`${styles.navItem} ${isActive('/admin/images') ? styles.active : ''}`}
          >
            <span className={styles.navIcon}>🖼️</span>
            <span className={styles.navLabel}>Image Manager</span>
          </Link>

          <Link
            to="/admin/settings"
            className={`${styles.navItem} ${isActive('/admin/settings') ? styles.active : ''}`}
          >
            <span className={styles.navIcon}>⚙️</span>
            <span className={styles.navLabel}>Site Settings</span>
          </Link>
        </nav>

        <div className={styles.sidebarFooter}>
          <button onClick={handleLogout} className={styles.logoutButton}>
            <span className={styles.navIcon}>🚪</span>
            <span className={styles.navLabel}>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className={styles.mainContent}>{children}</main>
    </div>
  );
};

export default AdminLayout;
